import React, { Fragment } from "react";
import styled from "styled-components";
import Moment from "react-moment";
import { Link } from "react-router-dom";
import { gql } from "apollo-boost";
import * as colors from "../settings/colors";
import { Table, TableRow, TableCell } from "./Table";
import Text from "./Text";
import StatusLabel from "./StatusLabel";
import { contactDisplayName, urlRoot } from "../utils/helpers";
import { dateComparator } from "../utils/sorters";
import joinPath from "../utils/join-path";

export const columnSizing = "1fr 1fr 1fr";

export const JobName = styled(Link)`
  color: ${p => p.color || colors.TEXT};
  text-decoration: none;
  font-size: 16px;

  &:hover {
    color: ${colors.BLUE};
  }
`;

export const JobDetails = styled.div`
  color: ${colors.TEXT_SECONDARY};
  font-size: 13px;
  padding-top: 3px;
`;

export const LargeStatusLabel = styled(StatusLabel)`
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;
`;

export const FranchiseInfo = ({ franchise, ...otherProps }) => {
  if (!franchise) return null;

  return (
    <Text muted {...otherProps}>
      <code>
        <b>{franchise.assignedInHouseTty}</b>
      </code>{" "}
      {franchise.name}
    </Text>
  );
};

const DraftsmanDetails = ({ draftsman }) => {
  if (!draftsman) return <Text muted>No draftsman</Text>;

  return (
    <div>
      {draftsman.name}
      {draftsman.phone && (
        <Text muted>
          <br />
          {draftsman.phone}
        </Text>
      )}
    </div>
  );
};

const ServiceLabelLink = ({ to, jobLink, service, active = false }) => {
  return (
    <Link to={`${jobLink}/services/${service.id}`}>
      {active ? (
        <LargeStatusLabel status={service.status}>
          {({ statusText }) => (
            <Fragment>
              <b>{service.serviceType}</b>
              <br />
              {service.status === "in_production" &&
              service.postedTurnaroundDate ? (
                <Moment format="MMM D, YYYY" utc>
                  {service.postedTurnaroundDate}
                </Moment>
              ) : (
                statusText
              )}
            </Fragment>
          )}
        </LargeStatusLabel>
      ) : (
        <StatusLabel status={service.status} text={service.serviceType} />
      )}
    </Link>
  );
};

export const ContractInfo = ({ job: { contractNumber } }) => {
  if (!contractNumber || contractNumber === "") {
    return null;
  }

  return (
    <span>
      <b>{contractNumber}</b>
      <span style={{ color: colors.TEXT_TERTIARY, padding: "0 4px" }}>|</span>
    </span>
  );
};

const renderServices = (services, jobLink) => {
  const orderedServices = services
    .slice()
    .sort(dateComparator("updatedAt", "desc"));

  const renderService = service => (
    <ServiceLabelLink
      key={service.id}
      service={service}
      jobLink={jobLink}
      active={true}
    />
  );

  return <div style={{ flex: "1" }}>{orderedServices.map(renderService)}</div>;
};

const JobsList = ({ jobs, match, ...otherProps }) => {
  const renderJob = job => {
    const jobLink = joinPath(urlRoot(match.url), `/jobs/${job.id}`);
    const deactivatedColor = job.deactivated && colors.TEXT_TERTIARY;

    return (
      <TableRow
        key={`job-${job.id}`}
        columns={columnSizing}
        style={{
          backgroundColor: job.archived ? colors.DIVIDER : "transparent"
        }}
      >
        <TableCell>
          <div>
            <JobName to={jobLink} color={deactivatedColor}>
              {contactDisplayName(job.contact)}
            </JobName>
            <JobDetails>
              <ContractInfo job={job} />
              <FranchiseInfo franchise={job.franchise} />
            </JobDetails>
          </div>
        </TableCell>
        <TableCell>
          {job.services && renderServices(job.services, jobLink)}
        </TableCell>
        <TableCell>
          <DraftsmanDetails draftsman={job.draftsman} />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Table noSideBorders {...otherProps}>
      <TableRow header columns={columnSizing}>
        <TableCell />
        <TableCell>Services</TableCell>
        <TableCell>Draftsman</TableCell>
      </TableRow>
      {jobs.map(renderJob)}
    </Table>
  );
};

JobsList.fragments = {
  job: gql`
    fragment JobsListJob on Job {
      id
      contractNumber
      contractType
      archived
      contact {
        id
        firstName
        lastName
        otherFirstName
        otherLastName
      }
      franchise {
        id
        name
        assignedInHouseTty
      }
      services {
        id
        serviceType
        status
        postedTurnaroundDate
        updatedAt
      }
      draftsman {
        id
        name
        phone
      }
    }
  `
};

export default JobsList;



// WEBPACK FOOTER //
// ./src/components/JobsList.js